import { z } from "zod";
import { normalizeSymbol } from "./instrument";
import type { MarketId } from "./market";
import { applyBps, formatPrice, type Price, price } from "./money";
import { makeQuote, type Quote, TIMEFRAME_MS, TIMEFRAMES, type Timeframe } from "./quote";

/**
 * A recorded market day: the artifact a replay reads and a backtest steps
 * through.
 *
 * Two properties matter more than anything else in this file.
 *
 * **The bytes are canonical.** `serializeRecording` sorts symbols, sorts bars,
 * and writes keys in a fixed order, so the same logical recording produces the
 * same string on any machine. A recording whose serialization varied would
 * make "identical results twice" a claim about JSON key ordering rather than
 * about the OMS.
 *
 * **The spread is modeled, and the model is IN the file.** Historical bars are
 * OHLCV — there is no bid/ask in them. The execution model fills a buy at the
 * ask and a sell at the bid, so a spread has to come from somewhere, and the
 * honest place is a recorded per-symbol `halfSpreadBps` that travels with the
 * data rather than a constant buried in a provider. A spread invented at read
 * time, or worse a zero one, lets a paper round-trip break even with no price
 * movement — the most flattering possible bug in a paper-trading system, and
 * therefore the one least likely to be reported. See `assertQuoteSane`.
 */

export const RECORDING_FORMAT = "atc-recording/1";

/**
 * One bar, covering `[t, t + TIMEFRAME_MS[timeframe])`.
 *
 * `t` is epoch milliseconds at the bar's OPEN. The half-open interval is what
 * makes "which bar is current" answerable without a tie-break.
 */
export type RecordedBar = {
  t: number;
  open: Price;
  high: Price;
  low: Price;
  close: Price;
  volume: number;
};

export type RecordedSymbol = {
  symbol: string;
  /**
   * Half the bid-ask spread, in basis points of the bar close. Applied
   * symmetrically: `bid = close - h`, `ask = close + h`.
   *
   * Half rather than full so it can be an integer that `applyBps` takes
   * directly — a full-spread field would need its own halving and its own
   * rounding rule, which is a second place for money arithmetic to live.
   */
  halfSpreadBps: number;
  bars: readonly RecordedBar[];
};

export type Recording = {
  format: typeof RECORDING_FORMAT;
  /** The trading day, `YYYY-MM-DD`, in the venue's calendar. */
  date: string;
  /** Provenance only. Nothing branches on it — a replay never calls a vendor. */
  source: string;
  timeframe: Timeframe;
  /** Epoch ms. `start` is the first bar's open, `end` the last bar's close. */
  session: { start: number; end: number };
  /** Sorted by symbol. */
  symbols: readonly RecordedSymbol[];
};

// ---------------------------------------------------------------------------
// Wire schema
// ---------------------------------------------------------------------------

/** Same rule as orders: prices cross as decimal strings, never JSON numbers. */
const decimalString = z
  .string()
  .regex(/^\d+(\.\d+)?$/, "must be a decimal string, not a JSON number")
  .refine((s) => /[1-9]/.test(s), "must be greater than zero");

const barSchema = z.object({
  t: z.number().int(),
  o: decimalString,
  h: decimalString,
  l: decimalString,
  c: decimalString,
  v: z.number().int().nonnegative(),
});

const symbolSchema = z.object({
  symbol: z.string().min(1).max(16),
  // 0 is permitted and is a statement, not a default: it says this recording
  // has no spread and a round trip is free. The recorder never writes it.
  half_spread_bps: z.number().int().min(0).max(5_000),
  bars: z.array(barSchema),
});

const recordingSchema = z.object({
  format: z.literal(RECORDING_FORMAT),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  source: z.string().min(1).max(64),
  timeframe: z.enum(TIMEFRAMES),
  session: z.object({ start: z.number().int(), end: z.number().int() }),
  symbols: z.array(symbolSchema),
});

export type RecordingWire = z.infer<typeof recordingSchema>;

// ---------------------------------------------------------------------------
// Parse and serialize
// ---------------------------------------------------------------------------

/**
 * Validate and normalize. Throws with the offending field named.
 *
 * Sorting happens here as well as in `serializeRecording` so that a
 * hand-written or hand-edited recording behaves identically to a generated
 * one — `barAt` binary-searches and would silently return the wrong bar
 * against unsorted input.
 */
export function parseRecording(input: unknown): Recording {
  const wire = recordingSchema.parse(input);

  const symbols = wire.symbols
    .map((s) => ({
      symbol: normalizeSymbol(s.symbol),
      halfSpreadBps: s.half_spread_bps,
      bars: [...s.bars]
        .sort((a, b) => a.t - b.t)
        .map((b) => ({
          t: b.t,
          open: price(b.o),
          high: price(b.h),
          low: price(b.l),
          close: price(b.c),
          volume: b.v,
        })),
    }))
    .sort((a, b) => (a.symbol < b.symbol ? -1 : a.symbol > b.symbol ? 1 : 0));

  const duplicate = symbols.find((s, i) => i > 0 && s.symbol === symbols[i - 1]?.symbol);
  if (duplicate) {
    throw new TypeError(`recording lists ${duplicate.symbol} twice`);
  }

  return {
    format: wire.format,
    date: wire.date,
    source: wire.source,
    timeframe: wire.timeframe,
    session: wire.session,
    symbols,
  };
}

/**
 * The canonical bytes. Key order is written out literally rather than left to
 * object-literal order elsewhere in the file, because this function's output
 * is compared byte for byte and an accidental reorder would look like a data
 * change.
 */
export function serializeRecording(recording: Recording): string {
  const symbols = [...recording.symbols]
    .sort((a, b) => (a.symbol < b.symbol ? -1 : a.symbol > b.symbol ? 1 : 0))
    .map((s) => ({
      symbol: s.symbol,
      half_spread_bps: s.halfSpreadBps,
      bars: [...s.bars]
        .sort((a, b) => a.t - b.t)
        .map((b) => ({
          t: b.t,
          o: formatPrice(b.open),
          h: formatPrice(b.high),
          l: formatPrice(b.low),
          c: formatPrice(b.close),
          v: b.volume,
        })),
    }));

  return `${JSON.stringify(
    {
      format: recording.format,
      date: recording.date,
      source: recording.source,
      timeframe: recording.timeframe,
      session: { start: recording.session.start, end: recording.session.end },
      symbols,
    },
    null,
    2,
  )}\n`;
}

// ---------------------------------------------------------------------------
// Reading a recording at a moment in time
// ---------------------------------------------------------------------------

export function recordedSymbol(recording: Recording, symbol: string): RecordedSymbol | undefined {
  const wanted = normalizeSymbol(symbol);
  return recording.symbols.find((s) => s.symbol === wanted);
}

/**
 * The last bar that has FINISHED at `atMs`.
 *
 * The bar containing `atMs` is deliberately not eligible: its close has not
 * happened yet at that instant, and using it is lookahead bias. A backtest that
 * reads a price before it existed makes every strategy look better than it is,
 * and the error is invisible in the output — the numbers are simply too good.
 *
 * Returns undefined before the first bar has closed, which callers must treat
 * as "no quote yet" rather than as a missing symbol.
 */
export function barAt(
  recorded: RecordedSymbol,
  timeframe: Timeframe,
  atMs: number,
): RecordedBar | undefined {
  const span = TIMEFRAME_MS[timeframe];
  const bars = recorded.bars;

  // Binary search for the last bar whose close (t + span) is <= atMs.
  let lo = 0;
  let hi = bars.length - 1;
  let found: RecordedBar | undefined;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const bar = bars[mid] as RecordedBar;
    if (bar.t + span <= atMs) {
      found = bar;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  return found;
}

/**
 * Bid and ask around a bar's close.
 *
 * Uses `applyBps`, so the rounding rule is the one in `money.ts` rather than a
 * second one invented here. Symmetric by construction, which is what
 * guarantees `bid <= ask` and keeps `assertQuoteSane` from ever seeing an
 * inverted quote out of a replay.
 */
export function spreadAround(close: Price, halfSpreadBps: number): { bid: Price; ask: Price } {
  return {
    bid: applyBps(close, -halfSpreadBps),
    ask: applyBps(close, halfSpreadBps),
  };
}

/**
 * The quote a recording implies at an instant.
 *
 * THE one definition of "what was the price at time t in this recording".
 *
 * Both readers of a recording call it: `ReplayProvider.snapshot`, which serves
 * a live replay, and the backtest harness, which steps a simulated clock. If
 * they each composed `barAt` and `spreadAround` themselves they would agree
 * today and drift later, and the drift would show up as a backtest whose
 * numbers do not reproduce what the terminal showed — which is the one thing
 * this whole iteration promises.
 *
 * Returns undefined before the first bar closes, and for a symbol the
 * recording does not carry. Callers omit rather than throw, per the port.
 */
export function quoteFromRecording(
  recording: Recording,
  symbol: string,
  atMs: number,
  source: string,
  /**
   * The venue the recording is of.
   *
   * Passed in rather than stored in the file: the recording format has
   * canonical bytes that a stability test pins, and every recording that exists
   * today is a US day. When a second market is recorded this becomes a field
   * and the format version moves with it — raised as `M4/E1` rather than
   * smuggled in here.
   */
  market: MarketId = "US",
): Quote | undefined {
  const recorded = recordedSymbol(recording, symbol);
  if (!recorded) return undefined;

  const bar = barAt(recorded, recording.timeframe, atMs);
  if (!bar) return undefined;

  const { bid, ask } = spreadAround(bar.close, recorded.halfSpreadBps);

  return makeQuote({
    symbol: recorded.symbol,
    market,
    bid: formatPrice(bid),
    ask: formatPrice(ask),
    // The close sits inside the spread by construction, so this cannot produce
    // the inverted quote `assertQuoteSane` refuses.
    last: formatPrice(bar.close),
    bidSize: bar.volume,
    askSize: bar.volume,
    // The recorded instant, not the wall clock. A replayed quote stamped `now`
    // would make every staleness check in the system lie.
    asOf: new Date(bar.t + TIMEFRAME_MS[recording.timeframe]),
    source,
  });
}

/** Every instant at which a bar closes, ascending. The harness steps these. */
export function closeTimes(recording: Recording): number[] {
  const span = TIMEFRAME_MS[recording.timeframe];
  const times = new Set<number>();
  for (const s of recording.symbols) {
    for (const b of s.bars) times.add(b.t + span);
  }
  return [...times].sort((a, b) => a - b);
}
